/**
 * Knowledge Base Workflow Configuration
 *
 * Organizes Trendy's capabilities into user-facing workflows
 * with example prompts that teach users how to interact effectively.
 *
 * Structure: Sections → Articles → Example prompts
 * Each prompt can be clicked to "Try it" in chat.
 */

// ─── Onboarding Cards (New to Trendy?) ─────────────────────────────────────

export const ONBOARDING_CARDS = [
  {
    id: 'first-chat',
    title: 'Your First Chat',
    description: 'Type a question in plain English — Trendy understands context.',
    example: "What's our cash position today?",
    icon: '💬',
  },
  {
    id: 'ask-about-customer',
    title: 'Ask About a Customer',
    description: 'Look up any customer by name, ID, or partial match.',
    example: 'Tell me about customer PMI',
    icon: '👥',
  },
  {
    id: 'run-playbook',
    title: 'Run a Playbook',
    description: 'Use the Playbooks tab on the left for step-by-step workflows.',
    example: 'Show me available playbooks',
    icon: '📋',
  },
  {
    id: 'universal-search',
    title: 'Search Across Epicor',
    description: 'Find anything across customers, parts, quotes, orders, and jobs.',
    example: 'Universal search for Beyer Brown',
    icon: '🔍',
  },
]

// ─── Workflow Sections ──────────────────────────────────────────────────────

export const WORKFLOW_SECTIONS = [
  {
    id: 'customers',
    title: 'Look Up Customers & Contacts',
    icon: '👥',
    color: 'emerald',
    description: 'Find customer details, contacts, ship-to addresses, and relationships',
    articles: [
      {
        title: 'Find a customer',
        description: 'Search by name, customer ID, city, or phone number',
        prompts: [
          'Search for customer PMI',
          'Find all customers in California',
          'Who is customer 1234?',
        ],
        tips: 'Trendy searches name, ID, address, city, state, phone, and email — use whatever you know.',
      },
      {
        title: 'Get customer contacts & ship-to addresses',
        description: 'Pull contact lists and shipping locations for any customer',
        prompts: [
          'Show me contacts for Beyer Brown',
          'What ship-to addresses does PMI have?',
          'Get the full customer 360 for A&W',
        ],
        tips: 'Customer 360 gives you everything at once: contacts, orders, AR balance, credit status, and notes.',
      },
      {
        title: 'Check credit status & AR balance',
        description: 'See what a customer owes, credit limits, and hold status',
        prompts: [
          "What's PMI's AR balance?",
          'Show me credit holds',
          "What's Beyer Brown's credit status?",
        ],
      },
      {
        title: 'Customer profitability',
        description: 'Revenue, costs, and margin analysis by customer',
        prompts: [
          'Show me customer profitability for PMI',
          'Which customers are most profitable?',
        ],
      },
    ],
  },
  {
    id: 'quoting',
    title: 'Quoting & Pricing',
    icon: '📝',
    color: 'blue',
    description: 'Create quotes, analyze pricing, benchmark against similar products',
    articles: [
      {
        title: 'Look up a quote',
        description: 'Find quotes by number, customer name, or customer part number',
        prompts: [
          'Show me quote 12345',
          'Find quotes for PMI',
          'Search quotes for part number ABC-100',
        ],
        tips: 'You can search by quote number, customer name, customer part number (XPartNum), or part description.',
      },
      {
        title: 'Benchmark pricing',
        description: 'Compare a quote against historical pricing for similar products',
        prompts: [
          'Benchmark quote 12345',
          'How does our pricing for PMI compare to similar quotes?',
        ],
        tips: 'Benchmarking checks recent win/loss data, material costs, and margin targets for similar parts.',
      },
      {
        title: 'Check if a quote became an order',
        description: 'See whether quotes have converted to sales orders',
        prompts: [
          'Did quote 12345 convert to an order?',
          'Show me quotes for PMI that turned into orders',
          'Which of our recent quotes have been won?',
        ],
        tips: 'Trendy checks the order line detail for QuoteNum linkage — it will tell you honestly if it hasn\'t checked yet.',
      },
      {
        title: 'Find expiring quotes',
        description: 'See quotes approaching their expiration date',
        prompts: [
          'Show me quotes expiring this month',
          'Which quotes expire in the next 2 weeks?',
        ],
      },
    ],
  },
  {
    id: 'orders-shipping',
    title: 'Orders & Shipping',
    icon: '📦',
    color: 'indigo',
    description: 'Track orders, check shipment status, create shipping invoices',
    articles: [
      {
        title: 'Look up an order',
        description: 'Find orders by number, customer, or customer part number',
        prompts: [
          'Show me order 54321',
          "What orders does A&W have open?",
          'Search orders for part XYZ-200',
        ],
        tips: 'Order search covers order number, customer name, customer part number, and part description.',
      },
      {
        title: 'Check shipping schedule',
        description: 'See what\'s shipping this week or for a specific date range',
        prompts: [
          "What's shipping this week?",
          'Show me orders due to ship by Friday',
          "What's ready to ship?",
        ],
      },
      {
        title: 'Create a shipping invoice',
        description: 'Generate an invoice for a shipped pack — use the Playbook for guided steps',
        prompts: [
          'Create a shipping invoice for pack 12345',
          'I need to invoice shipment 67890',
        ],
        tips: 'The Shipping Invoice Playbook walks you through this step by step. Look for it in the Playbooks tab.',
      },
      {
        title: 'Track order balance & releases',
        description: 'Check remaining quantities, release dates, and fulfillment status',
        prompts: [
          "What's the balance on order 54321?",
          'Show me releases for order 54321',
        ],
      },
    ],
  },
  {
    id: 'production',
    title: 'Jobs & Production',
    icon: '🔧',
    color: 'amber',
    description: 'Track job status, costs, schedules, and shop floor activity',
    articles: [
      {
        title: 'Look up a job',
        description: 'Find jobs by number, part, or customer',
        prompts: [
          'Show me job J-12345',
          'Find jobs for part ABC-100',
          'What jobs are running for PMI?',
        ],
      },
      {
        title: 'Check job costs & margin',
        description: 'See actual vs. estimated costs, material, labor, and burden',
        prompts: [
          'What are the costs on job J-12345?',
          'Show me margin by job for this month',
          'Which jobs are over budget?',
        ],
        tips: 'Job cost details include material, labor, burden, and subcontract breakdowns with estimated vs. actual.',
      },
      {
        title: 'Find late or overrun jobs',
        description: 'Identify production issues before they become problems',
        prompts: [
          'Show me late jobs',
          'Which jobs are over budget?',
          'Check job overruns',
        ],
      },
      {
        title: 'Check production capacity',
        description: 'See resource utilization and scheduling',
        prompts: [
          "What's our production capacity this week?",
          'Show me the job schedule',
        ],
      },
    ],
  },
  {
    id: 'ar-finance',
    title: 'Collect & Track Money (AR)',
    icon: '💰',
    color: 'green',
    description: 'AR aging, cash receipts, invoicing, and collections',
    articles: [
      {
        title: 'Check AR aging',
        description: 'See outstanding receivables by aging bucket (current, 30, 60, 90+ days)',
        prompts: [
          'Show me AR aging',
          "What's PMI's AR aging?",
          'Who owes us the most right now?',
        ],
      },
      {
        title: 'Look up an invoice',
        description: 'Find invoices by number, customer, or date range',
        prompts: [
          'Show me invoice 98765',
          'Find invoices for PMI from last month',
          'Search for open invoices',
        ],
      },
      {
        title: 'Post a cash receipt',
        description: 'Record a payment received from a customer',
        prompts: [
          'Post a cash receipt for PMI',
          'Record payment of $5,000 from Beyer Brown check #1234',
        ],
        tips: 'Trendy will walk you through amount, check number, and which invoices to apply it to.',
      },
      {
        title: 'Daily cash position',
        description: 'See today\'s cash inflows, outflows, and net position',
        prompts: [
          "What's our cash position today?",
          'Show me daily cash report',
        ],
      },
      {
        title: 'Create deposit or miscellaneous invoice',
        description: 'Generate deposit invoices or misc charges',
        prompts: [
          'Create a deposit invoice for order 54321',
          'I need a miscellaneous invoice for PMI',
        ],
      },
    ],
  },
  {
    id: 'ap-purchasing',
    title: 'Pay Bills & Manage Vendors (AP)',
    icon: '💳',
    color: 'purple',
    description: 'AP aging, vendor invoices, purchase orders, and payments',
    articles: [
      {
        title: 'Check AP aging',
        description: 'See what you owe vendors by aging bucket',
        prompts: [
          'Show me AP aging',
          "What do we owe vendor XYZ?",
          "What's our AP aging summary?",
        ],
      },
      {
        title: 'Find invoices due for payment',
        description: 'See upcoming AP invoices that need to be paid',
        prompts: [
          'What AP invoices are due this week?',
          'Show me invoices due in the next 30 days',
        ],
      },
      {
        title: 'Look up a PO',
        description: 'Find purchase orders by number or vendor',
        prompts: [
          'Show me PO 4567',
          'Find POs for vendor ABC Company',
          'Search for open purchase orders',
        ],
      },
      {
        title: 'Look up a vendor',
        description: 'Find vendor details, contacts, and payment history',
        prompts: [
          'Tell me about vendor 5678',
          'Search for vendor Acme Supply',
        ],
      },
    ],
  },
  {
    id: 'parts-inventory',
    title: 'Parts & Inventory',
    icon: '🏭',
    color: 'orange',
    description: 'Part lookups, inventory levels, costs, and where-used analysis',
    articles: [
      {
        title: 'Look up a part',
        description: 'Find parts by number, description, or search keyword',
        prompts: [
          'Show me part ABC-100',
          'Search for parts matching "foam"',
          'Find parts with keyword "chair"',
        ],
      },
      {
        title: 'Check inventory',
        description: 'See on-hand quantities, warehouse locations, and recent transactions',
        prompts: [
          "What's the inventory for part ABC-100?",
          'Show me inventory for foam parts',
          'Part transactions for ABC-100 this month',
        ],
      },
      {
        title: 'Part costs & where used',
        description: 'See standard costs, actual costs, and which jobs/orders use this part',
        prompts: [
          'What does part ABC-100 cost?',
          'Where is part ABC-100 used?',
        ],
      },
    ],
  },
  {
    id: 'documents-email',
    title: 'Documents & Email',
    icon: '📄',
    color: 'slate',
    description: 'Generate PDFs, send emails, search Outlook and SharePoint',
    articles: [
      {
        title: 'Generate a document',
        description: 'Create PDFs for quotes, orders, invoices, AR statements, and more',
        prompts: [
          'Generate a quote PDF for quote 12345',
          'Create an AR statement for PMI',
          'Generate a packing list for shipment 67890',
        ],
        tips: 'Available documents: Quote PDF, Order Confirmation, AR Statement, Job Traveler, Packing List, RFQ.',
      },
      {
        title: 'Send an email',
        description: 'Draft and send emails through Outlook (requires M365 connection)',
        prompts: [
          'Email the AR statement to PMI',
          'Send order confirmation for order 54321 to the customer',
        ],
        tips: 'You need to connect your Microsoft 365 account first. Look for the M365 button in the sidebar.',
      },
      {
        title: 'Search Outlook emails',
        description: 'Find emails about a customer, order, or topic',
        prompts: [
          'Search my emails about PMI',
          'Find emails about order 54321',
          'Search Outlook for "shipping delay"',
        ],
      },
      {
        title: 'Find attachments on records',
        description: 'View files attached to orders, quotes, jobs, parts, or customers in Epicor',
        prompts: [
          'Show me attachments on order 54321',
          'What files are attached to quote 12345?',
          'Find photos for part ABC-100',
        ],
      },
    ],
  },
  {
    id: 'intelligence',
    title: 'Document & Inbox Intelligence',
    icon: '🧠',
    color: 'sky',
    description: 'Read documents, scan your inbox, and connect what Trendy finds to Epicor automatically',
    articles: [
      {
        title: 'Read & interpret a document',
        description: 'Upload a PDF, photo, or scan — Trendy uses AI vision to extract every data point',
        prompts: [
          'Read the attached quote from PMI and summarize it',
          'What does this packing slip say?',
          'Interpret this vendor invoice and pull out the line items',
        ],
        tips: 'Works with PDFs, photos, scans, and screenshots. Trendy reads tables, handwriting, and fine print.',
      },
      {
        title: 'Cross-reference a document against Epicor',
        description: 'Trendy matches entities it finds (PO#, customers, parts) against live Epicor data',
        prompts: [
          'Cross-reference this PO against our Epicor records',
          'Check if the prices on this quote match our system',
          'Verify this invoice against the original order',
        ],
        tips: 'Trendy matches PO numbers, SO numbers, customer names, part numbers, and dollar amounts — then flags discrepancies.',
      },
      {
        title: 'Compare two documents',
        description: 'Diff two versions of a quote, spec, or contract side by side',
        prompts: [
          'Compare the old quote with the revised version they just sent',
          'What changed between these two spec sheets?',
        ],
      },
      {
        title: 'Scan your inbox',
        description: 'Trendy scans your Outlook inbox, extracts PO#s, dollar amounts, and classifies urgency',
        prompts: [
          'Scan my inbox for anything that needs attention',
          'Check my inbox for emails about purchase orders',
          'Scan my emails from this week with attachments',
        ],
        tips: 'Regex catches PO#s, SO#s, invoices, and dollar amounts instantly. AI classifies intent and urgency.',
      },
      {
        title: 'Match emails to Epicor records',
        description: 'After scanning, Trendy links each email to matching customers, orders, and jobs in Epicor',
        prompts: [
          'Scan my inbox and match everything to Epicor',
          'Which of my recent emails are about open orders?',
        ],
        tips: 'Uses the same matching engine as Document Intelligence — PO#s, customer names, and part numbers all resolve.',
      },
      {
        title: 'Get a morning inbox digest',
        description: 'A prioritized summary of your inbox grouped by urgency, customer, or action needed',
        prompts: [
          'Give me my morning email digest',
          'Summarize my inbox grouped by customer',
          'What emails need action today?',
        ],
      },
      {
        title: 'Process an email attachment',
        description: 'Open an attachment from an email and run it through Document Intelligence',
        prompts: [
          'Open the PDF attached to that PO email and cross-reference it',
          'Read the attachment on the latest email from PMI',
        ],
        tips: 'Combines Inbox + Document Intelligence — scans the email, downloads the attachment, reads it, and matches to Epicor.',
      },
    ],
  },
  {
    id: 'analytics',
    title: 'Reports & Analytics',
    icon: '📊',
    color: 'teal',
    description: 'Cash position, sales backlog, profitability, and operational dashboards',
    articles: [
      {
        title: 'Daily cash position',
        description: 'See cash inflows, outflows, and net position',
        prompts: [
          "What's our cash position today?",
          'Show me the cash report',
        ],
      },
      {
        title: 'Sales backlog',
        description: 'Open orders, pipeline value, and shipment timeline',
        prompts: [
          'Show me the sales backlog',
          "What's in our order pipeline?",
        ],
      },
      {
        title: 'Margin analysis',
        description: 'Profitability by job, customer, or product',
        prompts: [
          'Show me margin by job this month',
          'Customer profitability for PMI',
          'Which products have the best margins?',
        ],
      },
      {
        title: 'Export to spreadsheet',
        description: 'Export any data Trendy shows you into a downloadable spreadsheet',
        prompts: [
          'Export AR aging to a spreadsheet',
          'Download the sales backlog as Excel',
        ],
        tips: 'After any query, just ask "export that to a spreadsheet" and Trendy will create a downloadable file.',
      },
    ],
  },
  {
    id: 'universal-search',
    title: 'Universal Search',
    icon: '🔍',
    color: 'rose',
    description: 'Search across customers, parts, quotes, orders, and jobs in one command',
    articles: [
      {
        title: 'Search across everything',
        description: 'Find a customer, part, quote, order, or job with a single search',
        prompts: [
          'Universal search for PMI',
          'Universal search for Beyer Brown',
          'Universal search for ABC-100',
        ],
        tips: 'Universal search runs 7 queries in parallel across customers, parts, quote headers, quote lines, order headers, order lines, and jobs. Use it when you\'re not sure where something lives.',
      },
      {
        title: 'Search by customer part number',
        description: 'Find quotes and orders by the customer\'s part number (XPartNum)',
        prompts: [
          'Search quotes for customer part number 2024-FOAM-01',
          'Find orders with customer part ABC-CHAIR',
        ],
        tips: 'Customer part numbers are stored on quote lines and order lines. Trendy searches the XPartNum field.',
      },
    ],
  },
]

// ─── Prompting Tips ─────────────────────────────────────────────────────────

export const PROMPTING_TIPS = [
  {
    title: 'Be specific',
    bad: 'Show me invoices',
    good: 'Show me open invoices for PMI from March',
    why: 'Specific queries help Trendy pull exactly what you need without follow-up questions.',
  },
  {
    title: 'Use names and numbers',
    bad: 'Check that customer',
    good: 'Check AR aging for Beyer Brown',
    why: 'Trendy can search by customer name, ID, order number, part number, or job number.',
  },
  {
    title: 'Ask follow-ups naturally',
    bad: '(start a new chat for each question)',
    good: 'Now show me their open orders → And export that to Excel',
    why: 'Trendy remembers context within a conversation. Build on previous answers.',
  },
  {
    title: 'Say "universal search" when unsure',
    bad: '(searching quotes, then orders, then jobs separately)',
    good: 'Universal search for Auberge',
    why: 'This searches across all entity types at once — great when you don\'t know where to look.',
  },
]

// ─── Color utility ──────────────────────────────────────────────────────────

export const SECTION_COLORS = {
  emerald: {
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200 dark:border-emerald-800',
    text: 'text-emerald-700 dark:text-emerald-400',
    badge: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
    button: 'hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
  },
  blue: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-700 dark:text-blue-400',
    badge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
    button: 'hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  },
  indigo: {
    bg: 'bg-indigo-50 dark:bg-indigo-900/20',
    border: 'border-indigo-200 dark:border-indigo-800',
    text: 'text-indigo-700 dark:text-indigo-400',
    badge: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300',
    button: 'hover:bg-indigo-50 dark:hover:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400',
  },
  amber: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    text: 'text-amber-700 dark:text-amber-400',
    badge: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
    button: 'hover:bg-amber-50 dark:hover:bg-amber-900/20 text-amber-600 dark:text-amber-400',
  },
  green: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-green-700 dark:text-green-400',
    badge: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
    button: 'hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600 dark:text-green-400',
  },
  purple: {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    border: 'border-purple-200 dark:border-purple-800',
    text: 'text-purple-700 dark:text-purple-400',
    badge: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
    button: 'hover:bg-purple-50 dark:hover:bg-purple-900/20 text-purple-600 dark:text-purple-400',
  },
  orange: {
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    border: 'border-orange-200 dark:border-orange-800',
    text: 'text-orange-700 dark:text-orange-400',
    badge: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
    button: 'hover:bg-orange-50 dark:hover:bg-orange-900/20 text-orange-600 dark:text-orange-400',
  },
  slate: {
    bg: 'bg-slate-50 dark:bg-slate-900/20',
    border: 'border-slate-200 dark:border-slate-700',
    text: 'text-slate-700 dark:text-slate-400',
    badge: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
    button: 'hover:bg-slate-50 dark:hover:bg-slate-800/40 text-slate-600 dark:text-slate-400',
  },
  teal: {
    bg: 'bg-teal-50 dark:bg-teal-900/20',
    border: 'border-teal-200 dark:border-teal-800',
    text: 'text-teal-700 dark:text-teal-400',
    badge: 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300',
    button: 'hover:bg-teal-50 dark:hover:bg-teal-900/20 text-teal-600 dark:text-teal-400',
  },
  rose: {
    bg: 'bg-rose-50 dark:bg-rose-900/20',
    border: 'border-rose-200 dark:border-rose-800',
    text: 'text-rose-700 dark:text-rose-400',
    badge: 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300',
    button: 'hover:bg-rose-50 dark:hover:bg-rose-900/20 text-rose-600 dark:text-rose-400',
  },
  sky: {
    bg: 'bg-sky-50 dark:bg-sky-900/20',
    border: 'border-sky-200 dark:border-sky-800',
    text: 'text-sky-700 dark:text-sky-400',
    badge: 'bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300',
    button: 'hover:bg-sky-50 dark:hover:bg-sky-900/20 text-sky-600 dark:text-sky-400',
  },
}
